import { RolodexEntryType } from 'types'
import { z } from 'zod'

export const MetadataFieldAuditSchema = z.object({
  generated_value: z.string().nullable().optional(),
  user_value: z.string().nullable().optional(),
})

export const DocumentMetadataUserEditValuesSchema = z.object({
  system: z.string().nullable().optional(),
  vendor_name: z.string().nullable().optional(),
  document_type: z.string().nullable().optional(),
  drawing_number: z.string().nullable().optional(),
  revision_code: z.string().nullable().optional(),
  revision_date: z.string().nullable().optional(),
})

export const DocumentMetadataUserEditLogEntrySchema = z.object({
  edited_at: z.string(),
  edited_by: z.string(),
  system: MetadataFieldAuditSchema.optional().nullable(),
  vendor_name: MetadataFieldAuditSchema.optional().nullable(),
  document_type: MetadataFieldAuditSchema.optional().nullable(),
  drawing_number: MetadataFieldAuditSchema.optional().nullable(),
  revision_code: MetadataFieldAuditSchema.optional().nullable(),
  revision_date: MetadataFieldAuditSchema.optional().nullable(),
})

export const DocumentMetadataUserEditDataSchema = z.looseObject({
  document_id: z.string(),
  system_document_entry_id: z.string().nullable().optional(),
  values: DocumentMetadataUserEditValuesSchema,
  edit_log: z
    .array(DocumentMetadataUserEditLogEntrySchema)
    .optional()
    .nullable(),
})

export const DocumentMetadataUserEditEntrySchema = z.looseObject({
  entry: z.looseObject({
    id: z.string(),
    type: z.literal(RolodexEntryType.DOCUMENT_METADATA_USER_EDIT),
    data: DocumentMetadataUserEditDataSchema,
    tags: z.record(z.string(), z.union([z.string(), z.null()])).optional(),
    updated_at: z.string().optional(),
    last_updated_by: z.string().optional(),
  }),
  links: z.record(z.string(), z.unknown()).optional(),
})

export const DocumentPageMetadataUserEditValuesSchema = z.object({
  page_classification: z.string().nullable().optional(),
  page_classification_decision_method: z.string().nullable().optional(),
  issuer_drawing_number: z.string().nullable().optional(),
  secondary_drawing_number: z.string().nullable().optional(),
  sheet_number: z.string().nullable().optional(),
  latest_revision: z.string().nullable().optional(),
  latest_revision_date: z.string().nullable().optional(),
  system: z.string().nullable().optional(),
  document_type: z.string().nullable().optional(),
  document_title: z.string().nullable().optional(),
  issuer_company_name: z.string().nullable().optional(),
  issuer_company_role: z.string().nullable().optional(),
  viewing_rotation: z.string().nullable().optional(),
  is_revision_controlled_drawing: z.string().nullable().optional(),
  design_facility: z.string().nullable().optional(),
})

export const DocumentPageMetadataUserEditLogEntrySchema = z.object({
  edited_at: z.string(),
  edited_by: z.string(),
  page_classification: MetadataFieldAuditSchema.optional().nullable(),
  page_classification_decision_method:
    MetadataFieldAuditSchema.optional().nullable(),
  issuer_drawing_number: MetadataFieldAuditSchema.optional().nullable(),
  secondary_drawing_number: MetadataFieldAuditSchema.optional().nullable(),
  sheet_number: MetadataFieldAuditSchema.optional().nullable(),
  latest_revision: MetadataFieldAuditSchema.optional().nullable(),
  latest_revision_date: MetadataFieldAuditSchema.optional().nullable(),
  system: MetadataFieldAuditSchema.optional().nullable(),
  document_type: MetadataFieldAuditSchema.optional().nullable(),
  document_title: MetadataFieldAuditSchema.optional().nullable(),
  issuer_company_name: MetadataFieldAuditSchema.optional().nullable(),
  issuer_company_role: MetadataFieldAuditSchema.optional().nullable(),
  viewing_rotation: MetadataFieldAuditSchema.optional().nullable(),
  is_revision_controlled_drawing:
    MetadataFieldAuditSchema.optional().nullable(),
  design_facility: MetadataFieldAuditSchema.optional().nullable(),
})

export const DocumentPageMetadataUserEditCommentSchema = z.object({
  text: z.string(),
  created_by: z.string(),
  created_at: z.string(),
})

export const documentPageMetadataUserEditStatusSchema = z.enum([
  'confirmed',
  'deferred',
])

export const DocumentPageMetadataUserEditDataSchema = z.looseObject({
  document_id: z.string(),
  page_id: z.string(),
  page_num: z.coerce.number().nullable().optional(),
  system_document_page_entry_id: z.string().nullable().optional(),
  values: DocumentPageMetadataUserEditValuesSchema.optional().nullable(),
  edit_log: z
    .array(DocumentPageMetadataUserEditLogEntrySchema)
    .optional()
    .nullable(),
  status: documentPageMetadataUserEditStatusSchema.optional().nullable(),
  defer_reason: z.string().optional().nullable(),
  source: z.enum(['editor', 'labeler', 'reviewer']).optional().nullable(),
  surface: z.enum(['sidebar', 'eval']).optional().nullable(),
  generated_values:
    DocumentPageMetadataUserEditValuesSchema.optional().nullable(),
  comments: z
    .array(DocumentPageMetadataUserEditCommentSchema)
    .optional()
    .nullable(),
  should_have_metadata: z.boolean().optional().nullable(),
  pipeline_config_id: z.string().optional().nullable(),
  blind_qa: z.boolean().optional().nullable(),
})

export const DocumentPageMetadataUserEditEntrySchema = z.looseObject({
  created_by: z.string().optional(),
  entry: z.looseObject({
    id: z.string(),
    type: z.literal(RolodexEntryType.DOCUMENT_PAGE_METADATA_USER_EDIT),
    data: DocumentPageMetadataUserEditDataSchema,
    tags: z.record(z.string(), z.union([z.string(), z.null()])).optional(),
    created_at: z.string().optional(),
    created_by: z.string().optional(),
    updated_at: z.string().optional(),
    last_updated_by: z.string().optional(),
  }),
  links: z.record(z.string(), z.unknown()).optional(),
})

export type MetadataFieldAudit = z.infer<typeof MetadataFieldAuditSchema>
export type DocumentMetadataUserEditValues = z.infer<
  typeof DocumentMetadataUserEditValuesSchema
>
export type DocumentMetadataUserEditLogEntry = z.infer<
  typeof DocumentMetadataUserEditLogEntrySchema
>
export type DocumentMetadataUserEditEntry = z.infer<
  typeof DocumentMetadataUserEditEntrySchema
>
export type DocumentPageMetadataUserEditValues = z.infer<
  typeof DocumentPageMetadataUserEditValuesSchema
>
export type DocumentPageMetadataUserEditLogEntry = z.infer<
  typeof DocumentPageMetadataUserEditLogEntrySchema
>
export type DocumentPageMetadataUserEditComment = z.infer<
  typeof DocumentPageMetadataUserEditCommentSchema
>
export type DocumentPageMetadataUserEditEntry = z.infer<
  typeof DocumentPageMetadataUserEditEntrySchema
>
export type DocumentPageMetadataUserEditStatus = z.infer<
  typeof documentPageMetadataUserEditStatusSchema
>
export type DocumentPageMetadataUserEditSource = NonNullable<
  z.infer<typeof DocumentPageMetadataUserEditDataSchema>['source']
>
export type DocumentPageMetadataUserEditSurface = NonNullable<
  z.infer<typeof DocumentPageMetadataUserEditDataSchema>['surface']
>

export type DocumentMetadataUserEditValueKey =
  keyof DocumentMetadataUserEditValues

export const DOCUMENT_METADATA_USER_EDIT_VALUE_KEYS =
  DocumentMetadataUserEditValuesSchema.keyof().options

export type DocumentPageMetadataUserEditValueKey =
  keyof DocumentPageMetadataUserEditValues

export const DOCUMENT_PAGE_METADATA_USER_EDIT_VALUE_KEYS =
  DocumentPageMetadataUserEditValuesSchema.keyof().options

export function parseDocumentMetadataUserEditEntries(
  data: unknown
): DocumentMetadataUserEditEntry[] {
  if (!Array.isArray(data)) return []
  const parsed: DocumentMetadataUserEditEntry[] = []
  for (const item of data) {
    const result = DocumentMetadataUserEditEntrySchema.safeParse(item)
    if (result.success) {
      parsed.push(result.data)
    }
  }
  return parsed
}

export function parseDocumentPageMetadataUserEditEntries(
  data: unknown
): DocumentPageMetadataUserEditEntry[] {
  if (!Array.isArray(data)) return []
  const parsed: DocumentPageMetadataUserEditEntry[] = []
  for (const item of data) {
    const result = DocumentPageMetadataUserEditEntrySchema.safeParse(item)
    if (result.success) {
      parsed.push(result.data)
    }
  }
  return parsed
}
